<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PrimaryButton, SecondaryButton, DialogFlexibleInput, useSnackbarStore } from '@es-bill/es-bill-common-components';
import {
  MPM_ADMIN_ACTIONS,
  REASON_ENDED_VALUES,
  REVOCATION_STATUS_VALUES,
  runMpmStateFix,
} from '@/services/mpmAdminService';

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  action: { type: String, required: true },
  mpm: { type: Object, required: false, default: null },
});
const emit = defineEmits(['update:modelValue', 'done']);

const { t } = useI18n();
const snackbarStore = useSnackbarStore();

const dateStart = ref('');
const dateEnd = ref('');
const consentId = ref('');
const reasonEnded = ref(null);
const revocationStatus = ref(null);
const note = ref('');
const isRunning = ref(false);
const validationError = ref('');

watch(() => props.modelValue, (open) => {
  if (open) {
    dateStart.value = '';
    dateEnd.value = '';
    consentId.value = '';
    reasonEnded.value = null;
    revocationStatus.value = null;
    note.value = '';
    validationError.value = '';
  }
});

const fields = computed(() => MPM_ADMIN_ACTIONS[props.action]?.fields ?? []);
const hasField = (name) => fields.value.includes(name);

const reasonEndedItems = computed(() => REASON_ENDED_VALUES.map(value => ({
  title: t(`mpmStateFix.reasonEnded.${value}`), value,
})));
const revocationStatusItems = computed(() => REVOCATION_STATUS_VALUES.map(value => ({
  title: value, value,
})));

function close() {
  emit('update:modelValue', false);
}

async function confirm() {
  validationError.value = '';
  if (hasField('dateStart') && !dateStart.value) {
    validationError.value = t('mpmStateFix.dateRequired');
    return;
  }
  if (hasField('dateEnd') && !dateEnd.value) {
    validationError.value = t('mpmStateFix.dateRequired');
    return;
  }
  if (hasField('reasonEnded') && !reasonEnded.value) {
    validationError.value = t('mpmStateFix.reasonRequired');
    return;
  }

  isRunning.value = true;
  try {
    const result = await runMpmStateFix(props.action, props.mpm.mpmObjectId, {
      dateStart: dateStart.value,
      dateEnd: dateEnd.value,
      consentId: consentId.value,
      reasonEnded: reasonEnded.value,
      revocationStatus: revocationStatus.value,
      note: note.value,
    });
    snackbarStore.showSnackbar(t('mpmStateFix.success'),
      `${result.previousStatus} → ${result.newStatus}`, 'Success', 8000, []);
    close();
    emit('done', result);
  } catch (error) {
    const detail = error?.response?.data || error.message || '';
    snackbarStore.showSnackbar(t('mpmStateFix.failed'), String(detail), 'Error', 15000, []);
  } finally {
    isRunning.value = false;
  }
}

const buttonsConfig = computed(() => [
  {
    component: SecondaryButton,
    props: { size: 'large', class: 'w-100', text: t('cancel') },
    cols: '12',
    md: '6',
    onClick: close,
  },
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('confirm'), loading: isRunning.value },
    cols: '12',
    md: '6',
    onClick: confirm,
  },
]);
</script>

<template>
  <DialogFlexibleInput
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
    :title="t(`mpmStateFix.actions.${props.action}`)"
    :buttonsConfig="buttonsConfig"
    :persistent="true"
  >
    <template #input-slot>
      <div v-if="props.mpm" class="mb-4">
        <p class="font-weight-medium">{{ props.mpm.nameFriendly }}</p>
        <p class="text-body-2">{{ props.mpm.meteringPoint }}</p>
        <p class="text-body-2">{{ t('status') }}: {{ props.mpm.membershipStatus }}</p>
      </div>

      <p class="mb-4">{{ t(`mpmStateFix.descriptions.${props.action}`) }}</p>

      <v-text-field
        v-if="hasField('dateStart')"
        v-model="dateStart"
        type="date"
        :label="t('mpmStateFix.dateStart')"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      />
      <v-text-field
        v-if="hasField('dateEnd')"
        v-model="dateEnd"
        type="date"
        :label="t('mpmStateFix.dateEnd')"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      />
      <v-select
        v-if="hasField('reasonEnded')"
        v-model="reasonEnded"
        :items="reasonEndedItems"
        :label="t('mpmStateFix.reasonEndedLabel')"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      />
      <v-select
        v-if="hasField('revocationStatus')"
        v-model="revocationStatus"
        :items="revocationStatusItems"
        :label="t('mpmStateFix.revocationStatusLabel')"
        variant="outlined"
        density="comfortable"
        clearable
        class="mb-2"
      />
      <v-text-field
        v-if="hasField('consentId')"
        v-model="consentId"
        :label="t('mpmStateFix.consentId')"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      />
      <v-textarea
        v-if="hasField('note')"
        v-model="note"
        :label="t('mpmStateFix.note')"
        variant="outlined"
        rows="3"
        class="mb-2"
      />

      <p v-if="validationError" class="text-error">{{ validationError }}</p>
    </template>
  </DialogFlexibleInput>
</template>
